import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  FALLBACK_NOTICE_LANGUAGE,
  guaranteeNoticeAsset,
  resolveGuaranteeNoticeLanguage,
} from '../../legal/guarantee-notice-language';
import { type ILanguageState } from '../reducers/language/types';
import { type IUserState } from '../reducers/user/types';

export const useGuaranteeNoticeLanguage = (): string =>
  useSelector(
    ({
      Language,
      User,
    }: {
      Language?: ILanguageState;
      User?: IUserState;
    }): string =>
      resolveGuaranteeNoticeLanguage(
        Language?.languageType.code ?? FALLBACK_NOTICE_LANGUAGE,
        User?.countryType?.isoCode2 ?? null,
      ),
  );

export function GuaranteeNotice({
  languageCode,
}: {
  languageCode: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <figure className="guarantee-notice">
      <img
        src={`/static/${guaranteeNoticeAsset(languageCode)}`}
        alt={t('guaranteeNotice.altText')}
        width={595}
        height={842}
      />
    </figure>
  );
}

function GuaranteeNoticeDialog({
  languageCode,
  onClose,
}: {
  languageCode: string;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect((): (() => void) => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    return (): void => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  useEffect((): (() => void) => {
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        closeRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return (): void => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="confirm-dialog">
      <div
        className="confirm-dialog__backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="confirm-dialog__panel confirm-dialog__panel--notice"
        role="dialog"
        aria-modal="true"
        aria-label={t('guaranteeNotice.badgeLabel')}
      >
        <div className="confirm-dialog__actions confirm-dialog__actions--notice">
          <button
            type="button"
            ref={closeRef}
            className="confirm-dialog__button"
            onClick={onClose}
          >
            {t('guaranteeNotice.close')}
          </button>
        </div>
        <GuaranteeNotice languageCode={languageCode} />
      </div>
    </div>
  );
}

export function GuaranteeNoticeBadge(): React.JSX.Element {
  const { t } = useTranslation();
  const languageCode: string = useGuaranteeNoticeLanguage();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const close = useCallback((): void => setIsOpen(false), []);

  return (
    <>
      <button
        type="button"
        className="guarantee-notice-badge"
        aria-haspopup="dialog"
        onClick={(): void => setIsOpen(true)}
      >
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
        </svg>
        {t('guaranteeNotice.badgeLabel')}
      </button>
      {isOpen ? (
        <GuaranteeNoticeDialog languageCode={languageCode} onClose={close} />
      ) : null}
    </>
  );
}
